/**
 * Business-hours arithmetic — Step 31, and the plan's single highest-value
 * piece of pure logic: *"write it, prove it, then build the schema and the
 * tick engine on something already known to be correct."*
 *
 * Shared because both sides need the SAME answer. The engine computes a due
 * time and the browser renders the countdown beside it; two implementations
 * would disagree the first time a clock crossed a Sunday.
 *
 * ── Why this is harder than it looks ────────────────────────────────────
 *
 * "Eight business hours from 16:30 Friday" is not arithmetic on epoch
 * milliseconds. It has to walk a WALL CLOCK through a working week:
 *
 *   - Friday 16:30 + 8h on a Mon–Fri 09:00–17:00 calendar is 16:30 MONDAY
 *     (30 minutes of Friday, then 7h30 of Monday), not Saturday morning.
 *   - Across a DST boundary the answer must stay at the right wall-clock
 *     time. Europe/London's spring-forward means one calendar day contains
 *     23 real hours; a naïve `+ 8 * 3600_000` silently lands an hour out
 *     twice a year, in the direction nobody notices until a breach report
 *     is wrong.
 *   - Split shifts (09:00–12:00, 13:00–17:00) mean a lunch hour is not SLA
 *     time, so "one hour from 11:30" is 13:30.
 *   - A holiday, or a day with no shifts at all, contributes nothing and
 *     must be skipped without the loop spinning.
 *
 * So everything below converts to the calendar's own timezone, does the
 * walking there, and converts back — with no dependency, using `Intl` for
 * the offset lookups.
 */

/** `HH:MM` in the calendar's timezone. */
export interface Shift {
  start: string;
  end: string;
}

/**
 * A working week. `week[0]` is Sunday, matching `Date.getDay()`, so a
 * calendar can be indexed straight from a weekday without a lookup table.
 * An empty array means the desk is shut that day.
 */
export interface BusinessCalendar {
  /** IANA zone, e.g. `Europe/London`. Never a fixed offset — the whole point
   *  is to follow DST rather than pretend it does not happen. */
  timezone: string;
  week: Shift[][];
  /** `YYYY-MM-DD` dates in the calendar's own timezone, treated as closed. */
  holidays?: string[];
}

/** 24/7 — the sane default before anyone configures anything, and the
 *  behaviour a "respond within 4 hours" promise means when nobody has said
 *  what the working week is. */
export const ALWAYS_OPEN: BusinessCalendar = {
  timezone: 'UTC',
  week: Array.from({ length: 7 }, () => [{ start: '00:00', end: '24:00' }]),
};

const MINUTE = 60_000;
const DAY_MINUTES = 24 * 60;

/** Minutes since midnight for `HH:MM`. `24:00` is legal and means end-of-day. */
function minutesOf(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return 0;
  return Math.min(DAY_MINUTES, Number(m[1]) * 60 + Number(m[2]));
}

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** The wall clock in `timeZone` at an instant. */
export function zonedParts(ms: number, timeZone: string): Parts {
  const parts = formatter(timeZone).formatToParts(new Date(ms));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  // `24` at midnight is a documented Intl quirk with hour12:false.
  const hour = Number(get('hour')) % 24;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour,
    minute: Number(get('minute')),
    weekday: WEEKDAYS[get('weekday')] ?? 0,
  };
}

/** The zone's UTC offset, in ms, at an instant. */
function offsetAt(ms: number, timeZone: string): number {
  const p = zonedParts(ms, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  // Seconds are dropped by the formatter, so compare on the minute.
  return asUtc - Math.floor(ms / MINUTE) * MINUTE;
}

/**
 * A wall-clock time in a zone → the instant it refers to.
 *
 * The two-pass correction handles the transitions: the first guess uses the
 * offset at the *wrong* instant when the conversion straddles a change, so
 * the offset is re-read at the corrected instant and applied again. Times
 * that do not exist (the spring-forward gap) resolve forward, which is the
 * conventional and useful choice — an SLA never starts in a missing hour.
 */
export function instantFromWall(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const firstGuess = wallAsUtc - offsetAt(wallAsUtc, timeZone);
  const corrected = wallAsUtc - offsetAt(firstGuess, timeZone);
  return corrected;
}

/** `YYYY-MM-DD` for a wall-clock date, for holiday comparison. */
function dateKey(p: Parts): string {
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** The shifts open on the day `p` falls in — empty on a holiday or a closed
 *  day, which is what makes both cases one code path. */
function shiftsFor(p: Parts, cal: BusinessCalendar): Shift[] {
  if (cal.holidays?.includes(dateKey(p))) return [];
  return cal.week[p.weekday] ?? [];
}

/** Windows of open time on one day, as [startMin, endMin), sorted and with
 *  nonsense (end <= start) dropped rather than looping forever. */
function windowsFor(p: Parts, cal: BusinessCalendar): [number, number][] {
  return shiftsFor(p, cal)
    .map((s) => [minutesOf(s.start), minutesOf(s.end)] as [number, number])
    .filter(([a, b]) => b > a)
    .sort((a, b) => a[0] - b[0]);
}

/** Does the calendar ever open? Guards every loop below: a calendar with no
 *  hours at all would otherwise walk forward forever looking for one. */
export function calendarIsOpenEver(cal: BusinessCalendar): boolean {
  return cal.week.some((day) => day.some((s) => minutesOf(s.end) > minutesOf(s.start)));
}

/** A year of days is far past any real SLA and stops a pathological calendar
 *  (open one hour a year, say) from hanging a request. */
const MAX_DAYS = 366;

/**
 * Is this instant inside working hours?
 */
export function isWithinBusinessHours(ms: number, cal: BusinessCalendar): boolean {
  const p = zonedParts(ms, cal.timezone);
  const mins = p.hour * 60 + p.minute;
  return windowsFor(p, cal).some(([a, b]) => mins >= a && mins < b);
}

/**
 * Add business milliseconds to an instant, returning the instant the budget
 * runs out.
 *
 * Starting outside working hours moves to the next opening — a ticket raised
 * at 03:00 does not burn its SLA overnight.
 */
export function addBusinessMs(startMs: number, addMs: number, cal: BusinessCalendar): number {
  if (addMs <= 0) return startMs;
  if (!calendarIsOpenEver(cal)) {
    // Nothing to spend the budget in. Returning the start rather than
    // looping is the honest failure: the caller sees "due now", which is
    // visible, instead of a hang.
    return startMs;
  }

  let remaining = Math.ceil(addMs / MINUTE);
  let cursor = startMs;

  for (let guard = 0; guard < MAX_DAYS && remaining > 0; guard++) {
    const p = zonedParts(cursor, cal.timezone);
    const nowMins = p.hour * 60 + p.minute;

    for (const [openMin, closeMin] of windowsFor(p, cal)) {
      if (remaining <= 0) break;
      if (nowMins >= closeMin) continue; // window already passed today
      const from = Math.max(nowMins, openMin);
      const available = closeMin - from;
      if (available <= 0) continue;

      if (remaining <= available) {
        // Lands inside this window. Converting back through the WALL CLOCK
        // is what makes DST correct: `from + remaining` is a wall-clock
        // minute, and the instant it maps to depends on the offset that day.
        return instantFromWall(p.year, p.month, p.day, 0, from + remaining, cal.timezone);
      }
      remaining -= available;
      cursor = instantFromWall(p.year, p.month, p.day, 0, closeMin, cal.timezone);
    }

    // Move to 00:00 of the day AFTER the one just examined, in the
    // calendar's own zone.
    //
    // The bug this replaces: the advance ran unconditionally, so a window
    // ending at 24:00 (a 24/7 calendar) left the cursor already at the next
    // midnight — and then it advanced AGAIN, skipping a whole day. Caught by
    // the round-trip test, which is exactly what that test is for. Advancing
    // only when the cursor is still inside the examined day fixes it for
    // every calendar shape.
    cursor = Math.max(cursor, startOfNextDay(p, cal.timezone));
  }
  return cursor;
}

/** 00:00 of the day after `p`, in the calendar's zone. Built from the WALL
 *  CLOCK rather than by adding 24h, which would drift by an hour across a
 *  DST transition. `day + 1` may overflow the month; `Date.UTC` normalises
 *  that, and `instantFromWall` goes through it. */
function startOfNextDay(p: Parts, timeZone: string): number {
  return instantFromWall(p.year, p.month, p.day + 1, 0, 0, timeZone);
}

/**
 * Business milliseconds between two instants — how much of an SLA has
 * actually been consumed.
 */
export function businessMsBetween(fromMs: number, toMs: number, cal: BusinessCalendar): number {
  if (toMs <= fromMs) return 0;
  if (!calendarIsOpenEver(cal)) return 0;

  let total = 0;
  let cursor = fromMs;

  for (let guard = 0; guard < MAX_DAYS && cursor < toMs; guard++) {
    const p = zonedParts(cursor, cal.timezone);
    const dayStart = instantFromWall(p.year, p.month, p.day, 0, 0, cal.timezone);

    for (const [openMin, closeMin] of windowsFor(p, cal)) {
      const openAt = dayStart + openMin * MINUTE;
      const closeAt = dayStart + closeMin * MINUTE;
      const from = Math.max(cursor, openAt);
      const to = Math.min(toMs, closeAt);
      if (to > from) total += to - from;
    }

    cursor = Math.max(cursor + 1, startOfNextDay(p, cal.timezone));
  }
  return total;
}

/**
 * The state of one SLA clock, as the UI needs it.
 *
 * The server sends an ABSOLUTE `dueAt`, never "minutes remaining" — the plan
 * is explicit, and the reason is this file: business hours make time-
 * remaining non-linear, so a countdown computed in the browser would tick
 * through a closed Sunday and arrive at the wrong answer. When a clock is
 * paused the UI shows the due time, not a number sliding downwards.
 */
export interface SlaClockState {
  dueAt: number;
  /** Set while the clock is paused (on hold, awaiting approval). */
  frozenAt?: number | null;
  breached: boolean;
}

/** How a due time should read on a row: the one saturated colour on the row
 *  is reserved for this (Step 17). */
export type SlaUrgency = 'breached' | 'imminent' | 'ok' | 'paused';

export function slaUrgency(state: SlaClockState, now: number, imminentMs = 60 * MINUTE): SlaUrgency {
  if (state.frozenAt) return 'paused';
  if (state.breached || now >= state.dueAt) return 'breached';
  return state.dueAt - now <= imminentMs ? 'imminent' : 'ok';
}
